/**
 * Die Verwaltung fuer H&K. Kanzleien anlegen, Zugaenge vergeben, sehen,
 * wie viel gelesen wurde.
 *
 * Keine Mandanten, keine Auszuege, keine Betraege: H&K betreibt das
 * Werkzeug und liest nicht mit. Die Schnittstelle gibt es auch nicht her.
 */
import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler } from '../lib/api'
import { zeitpunkt } from '../lib/format'
import { Fenster } from '../teile/Fenster'

interface KanzleiZeile {
  id: string
  name: string
  aktiv: boolean
  erstellt_am: string
  loeschfrist_tage: number
  camt_version: string
  benutzer: number
  mandanten: number
  dokumente_monat: number
  seiten_monat: number
  exporte_monat: number
  ki_kosten_usd_monat: number
}

interface Zugang {
  id: string
  kanzlei_id: string | null
  name: string
  email: string
  rolle: 'verwaltung' | 'kanzlei'
  aktiv: boolean
  letzte_anmeldung: string | null
}

const kanzleiSpalten = { gridTemplateColumns: 'minmax(0, 1.4fr) 80px 90px 100px 80px 90px 110px 110px' }
const zugangSpalten = { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr) 150px 210px' }

export function VerwaltungSeite() {
  const abfragen = useQueryClient()
  const { data, isPending, error } = useQuery({
    queryKey: ['verwaltung'],
    queryFn: () => api.holen<{ kanzleien: KanzleiZeile[]; zugaenge: Zugang[] }>('/api/verwaltung/kanzleien'),
  })
  const [kanzleiOffen, setzeKanzleiOffen] = useState(false)
  const [zugangFuer, setzeZugangFuer] = useState<string | null | undefined>(undefined)
  const [passwort, setzePasswort] = useState<{ email: string; passwort: string } | null>(null)
  const [fehler, setzeFehler] = useState<string | null>(null)
  // Passwort zuruecksetzen und Stilllegen werfen Menschen aus der Anwendung. Erst fragen.
  const [rueckfrage, setzeRueckfrage] = useState<{ titel: string; text: string; knopf: string; aktion: () => Promise<void> } | null>(null)

  const kanzleiName = (id: string | null) => (id ? data?.kanzleien.find((k) => k.id === id)?.name ?? '' : 'Verwaltung (H&K)')

  async function zugangAendern(z: Zugang, aenderung: { aktiv?: boolean; neuesPasswort?: true }) {
    setzeFehler(null)
    try {
      const antwort = await api.aendern<{ passwort: string | null }>(`/api/verwaltung/zugaenge/${z.id}`, aenderung)
      if (antwort.passwort) setzePasswort({ email: z.email, passwort: antwort.passwort })
      await abfragen.invalidateQueries({ queryKey: ['verwaltung'] })
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Das hat nicht geklappt.')
    }
  }

  async function kanzleiUmschalten(k: KanzleiZeile) {
    setzeFehler(null)
    try {
      await api.aendern(`/api/verwaltung/kanzleien/${k.id}`, { aktiv: !k.aktiv })
      await abfragen.invalidateQueries({ queryKey: ['verwaltung'] })
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Das hat nicht geklappt.')
    }
  }

  return (
    <>
      {fehler && <div className="fehler abstand-unten">{fehler}</div>}
      {passwort && (
        <div className="bestaetigung abstand-unten passwort-anzeige">
          Einmalpasswort für {passwort.email}: <span className="zahl">{passwort.passwort}</span>
          <span className="leiser"> Wird nur jetzt angezeigt. Nach der ersten Anmeldung ändern lassen.</span>
          <button type="button" className="knopf still klein" onClick={() => setzePasswort(null)}>Ausblenden</button>
        </div>
      )}

      <div className="listenleiste">
        <div className="leiser">Mandanten und Auszüge der Kanzleien sind hier bewusst nicht sichtbar.</div>
        <button type="button" className="knopf" onClick={() => setzeKanzleiOffen(true)}>Kanzlei anlegen</button>
      </div>

      <div className="liste">
        <div className="listenkopf" style={kanzleiSpalten}>
          <div>Kanzlei</div>
          <div className="rechts">Zugänge</div>
          <div className="rechts">Mandanten</div>
          <div className="rechts">Dokumente</div>
          <div className="rechts">Seiten</div>
          <div className="rechts">Exporte</div>
          <div className="rechts">KI im Monat</div>
          <div />
        </div>
        {isPending && <div className="laedt">Wird geladen …</div>}
        {error && <div className="leer"><strong>Konnte nicht geladen werden.</strong></div>}
        {data?.kanzleien.map((k) => (
          <div key={k.id} className={`zeile${k.aktiv ? '' : ' abgeloest'}`} style={kanzleiSpalten}>
            <div>
              <div className="sache">{k.name}{!k.aktiv && <span className="marke">stillgelegt</span>}</div>
              <div className="nebensache">seit {zeitpunkt(k.erstellt_am).slice(0, 10)} · Löschfrist {k.loeschfrist_tage} Tage · camt .{k.camt_version}</div>
            </div>
            <div className="leise zahl rechts">{k.benutzer}</div>
            <div className="leise zahl rechts">{k.mandanten}</div>
            <div className="leise zahl rechts">{k.dokumente_monat}</div>
            <div className="leise zahl rechts">{k.seiten_monat}</div>
            <div className="leise zahl rechts">{k.exporte_monat}</div>
            <div className="leise zahl rechts">{k.ki_kosten_usd_monat.toFixed(2).replace('.', ',')} $</div>
            <div className="zeilenaktionen">
              <button type="button" className="knopf klein" onClick={() => setzeZugangFuer(k.id)}>Zugang</button>
              <button type="button" className="knopf still klein" onClick={() => (k.aktiv
                ? setzeRueckfrage({ titel: 'Kanzlei stilllegen', text: `Alle Zugänge von ${k.name} werden sofort abgemeldet und können sich nicht mehr anmelden, bis die Kanzlei wieder aktiviert wird. Daten bleiben erhalten.`, knopf: 'Stilllegen', aktion: () => kanzleiUmschalten(k) })
                : void kanzleiUmschalten(k))}>{k.aktiv ? 'Stilllegen' : 'Aktivieren'}</button>
            </div>
          </div>
        ))}
      </div>
      <p className="leiser abstand-oben-klein">Dokumente, Seiten, Exporte und KI-Kosten im laufenden Monat. Die Kosten sind eine Schätzung aus den Token, abgerechnet wird bei Anthropic.</p>

      <div className="listenleiste abstand-oben">
        <h2>Zugänge</h2>
        <button type="button" className="knopf" onClick={() => setzeZugangFuer(null)}>Zugang für H&K anlegen</button>
      </div>
      <div className="liste">
        <div className="listenkopf" style={zugangSpalten}>
          <div>Name</div>
          <div>E-Mail</div>
          <div>Kanzlei</div>
          <div>Letzte Anmeldung</div>
          <div />
        </div>
        {data?.zugaenge.map((z) => (
          <div key={z.id} className={`zeile${z.aktiv ? '' : ' abgeloest'}`} style={zugangSpalten}>
            <div className="sache">{z.name}{!z.aktiv && <span className="marke">stillgelegt</span>}</div>
            <div className="leise">{z.email}</div>
            <div className="leise">{kanzleiName(z.kanzlei_id)}</div>
            <div className="leiser zahl">{zeitpunkt(z.letzte_anmeldung)}</div>
            <div className="zeilenaktionen">
              <button type="button" className="knopf still klein" onClick={() => setzeRueckfrage({ titel: 'Neues Passwort', text: `${z.name} bekommt ein neues Einmalpasswort. Das bisherige gilt sofort nicht mehr, alle Anmeldungen dieser Person werden beendet.`, knopf: 'Neues Passwort erzeugen', aktion: () => zugangAendern(z, { neuesPasswort: true }) })}>Neues Passwort</button>
              <button type="button" className="knopf still klein" onClick={() => (z.aktiv
                ? setzeRueckfrage({ titel: 'Zugang stilllegen', text: `${z.name} wird sofort abgemeldet und kann sich nicht mehr anmelden, bis der Zugang wieder aktiviert wird.`, knopf: 'Stilllegen', aktion: () => zugangAendern(z, { aktiv: false }) })
                : void zugangAendern(z, { aktiv: true }))}>{z.aktiv ? 'Stilllegen' : 'Aktivieren'}</button>
            </div>
          </div>
        ))}
      </div>

      <Fenster titel={rueckfrage?.titel ?? ''} offen={rueckfrage !== null} aufSchliessen={() => setzeRueckfrage(null)}>
        {rueckfrage && (
          <div className="formular">
            <p className="leise">{rueckfrage.text}</p>
            <div className="formular-fuss">
              <button type="button" className="knopf still" onClick={() => setzeRueckfrage(null)}>Abbrechen</button>
              <button type="button" className="knopf gefuellt" onClick={() => { const a = rueckfrage.aktion; setzeRueckfrage(null); void a() }}>{rueckfrage.knopf}</button>
            </div>
          </div>
        )}
      </Fenster>

      {kanzleiOffen && <KanzleiAnlegen aufSchliessen={() => setzeKanzleiOffen(false)} />}
      {zugangFuer !== undefined && (
        <ZugangAnlegen
          kanzleiId={zugangFuer}
          kanzleiName={kanzleiName(zugangFuer)}
          aufSchliessen={() => setzeZugangFuer(undefined)}
          angelegt={(email, pw) => setzePasswort({ email, passwort: pw })}
        />
      )}
    </>
  )
}

function KanzleiAnlegen({ aufSchliessen }: { aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const [name, setzeName] = useState('')
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  async function absenden(e: FormEvent) {
    e.preventDefault()
    if (laeuft) return
    setzeLaeuft(true)
    try {
      await api.senden('/api/verwaltung/kanzleien', { name })
      await abfragen.invalidateQueries({ queryKey: ['verwaltung'] })
      aufSchliessen()
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Die Kanzlei konnte nicht angelegt werden.')
    } finally {
      setzeLaeuft(false)
    }
  }
  return (
    <Fenster titel="Kanzlei anlegen" offen aufSchliessen={aufSchliessen}>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler}</div>}
        <div>
          <label htmlFor="v-kanzlei">Name der Kanzlei</label>
          <input id="v-kanzlei" type="text" required autoFocus autoComplete="off" data-1p-ignore value={name} onChange={(e) => setzeName(e.target.value)} />
          <div className="hinweis">So erscheint sie im Profilmenü ihrer Zugänge.</div>
        </div>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft}>{laeuft ? 'Wird angelegt …' : 'Anlegen'}</button>
        </div>
      </form>
    </Fenster>
  )
}

function ZugangAnlegen({ kanzleiId, kanzleiName, aufSchliessen, angelegt }: { kanzleiId: string | null; kanzleiName: string; aufSchliessen: () => void; angelegt: (email: string, passwort: string) => void }) {
  const abfragen = useQueryClient()
  const [name, setzeName] = useState('')
  const [email, setzeEmail] = useState('')
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  async function absenden(e: FormEvent) {
    e.preventDefault()
    if (laeuft) return
    setzeLaeuft(true)
    try {
      const antwort = await api.senden<{ passwort: string }>('/api/verwaltung/zugaenge', { kanzleiId, name, email })
      await abfragen.invalidateQueries({ queryKey: ['verwaltung'] })
      angelegt(email.toLowerCase(), antwort.passwort)
      aufSchliessen()
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Der Zugang konnte nicht angelegt werden.')
    } finally {
      setzeLaeuft(false)
    }
  }
  return (
    <Fenster titel={`Zugang für ${kanzleiName}`} offen aufSchliessen={aufSchliessen}>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler}</div>}
        <div><label htmlFor="v-person">Vor- und Nachname</label><input id="v-person" type="text" required autoFocus autoComplete="off" data-1p-ignore value={name} onChange={(e) => setzeName(e.target.value)} /></div>
        <div><label htmlFor="v-mail">E-Mail</label><input id="v-mail" type="email" required autoComplete="off" value={email} onChange={(e) => setzeEmail(e.target.value)} /></div>
        <div className="hinweis">Das Passwort wird erzeugt und einmal angezeigt. Es wird nicht verschickt.</div>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft}>{laeuft ? 'Wird angelegt …' : 'Anlegen'}</button>
        </div>
      </form>
    </Fenster>
  )
}
